import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {InfoTimbratura} from "@commons/mapping/TimbraturaLavRecord";
import {FPNetDate, ObjectFactory} from "@commons/types";
import {merge} from "lodash";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {DateTime} from "luxon";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";

const isDefined = (value: any) => typeof value !== 'undefined';

export interface CoppiaTimbratureEx extends CoppiaTimbrature {
    posizioneEntrata?: number,
    posizioneUscita?: number,
    entrataManuale?: boolean,
    entrataRimossa?: boolean,
    uscitaManuale?: boolean,
    uscitaRimossa?: boolean,
}

type TipoOperazione = 0 | 1 | 2;
export type JSonManutenzioneTimbrature = {
    maskEffettive: number,
    timbratureWF: string[], // Timbrature risultanti, array di stringhe, ogni elemento corrisponde a una timbratura
    nulls: boolean[], // Posizioni nulle, array di boolean, ogni elemento rappresenta la posizioni editabile (true editabile, false => non editabile)
    operazioni: TipoOperazione[], // array di interi, ogni elemento rappresenta l'operazione eseguibile per la coppia
    manuali: (string | null)[], // Timbrature manuali, array di stringhe, ogni elemento corrisponde a una timbratura editata (null in corrispondenza di una timbratura effettiva)
    effettiveOriginali: Pick<CoppiaTimbrature, 'entrata' | 'uscita'>[], // copia delle effettive
    posizioneOriginali: (number | null)[], // posizione delle effettive
};

export interface ManutenzioneTimbrature {
    effettiveOriginali: Pick<CoppiaTimbrature, 'entrata' | 'uscita'>[], // array di {entrata:"hhmm", uscita:"hhmm"}
    arrayCoppie: CoppiaTimbratureEx[][], // array di array di una coppia (un solo elemento)
    effettiveManualiFlat: (string | null)[][], // array di array di stringhe (max due elementi)
    nullPos: (number | null)[][], // array di array di int (max due elementi)
    maskEffettive: number[], // array di int rappresentanti la mask della coppia corrispondente
    operazioneSuCoppia: TipoOperazione[], // array che contiene l' operazione concessa alla coppia corrispondente: 0 nessuna operazione, 1 rimuovi, 2 dividi
}

export class ManutenzioneTimbrature {
    static factory: ObjectFactory<ManutenzioneTimbrature> = (json?) => {
        return merge<ManutenzioneTimbrature, Partial<ManutenzioneTimbrature> | undefined>({
            effettiveOriginali: [],
            arrayCoppie: [],
            effettiveManualiFlat: [],
            nullPos: [],
            maskEffettive: [],
            operazioneSuCoppia: [],
        }, json);
    }

    static ripristinaDaEffettive = (MT: ManutenzioneTimbrature, currentDay: PresenzeGiornataPlusRecord) => {
        MT.effettiveOriginali = []; // array di {entrata:"hhmm", uscita:"hhmm"}
        MT.arrayCoppie = []; // array di array di una coppia (un solo elemento)
        MT.effettiveManualiFlat = []; // array di array di stringhe (max due elementi)
        MT.nullPos = []; // array di array di int (max due elementi)
        MT.maskEffettive = []; // array di int rappresentanti la mask della coppia corrispondente
        MT.operazioneSuCoppia = []; // array che contiene la coppia (se originale) altrimenti null

        const nulls = PresenzeGiornataPlusRecord.getStrisciataOriginale(currentDay, true).nullPos;
        let posizione = 0;
        // Normalizzo effettiveManualiFlat, potrebbe essere null oppure avere meno elementi di effettiveOriginali
        const effettiveManualiFlat = GestionePlus.adeguaEffManuali(currentDay, currentDay.effettiveManualiFlat);

        currentDay.effettiveOriginali.forEach(function (val, index) {
            // valorizzo maschera per coppia
            let mask = 0x03 << index * 2;
            mask = mask & currentDay.maskEffettive;
            mask = mask >> index * 2;
            MT.maskEffettive.push(mask);

            // Clono coppia timbratura originale!!!
            const clone: CoppiaTimbratureEx = CoppiaTimbrature.factory(val);
            clone.posizioneEntrata = posizione++;
            clone.posizioneUscita = posizione++;
            clone.entrataManuale = nulls[index * 2] !== null; // significativo solo per App, valuta se timbratura editabile
            clone.uscitaManuale = nulls[index * 2 + 1] !== null; // significativo solo per App, valuta se timbratura editabile
            clone.utilizzaEntrata = !!(mask & 0x01);
            clone.utilizzaUscita = !!(mask & 0x02);
            clone.entrataTerminale = clone.entrataManuale ? 'null' : '*';
            clone.uscitaTerminale = clone.uscitaManuale ? 'null' : '*';
            MT.effettiveOriginali.push({entrata: val.entrata, uscita: val.uscita}); // Salvo per riscontro in MonitorWF
            MT.arrayCoppie.push([clone]);

            let partial: any[];
            partial = []; // Valorizzo coppia <effettiveManualiFlat>
            // N.B. Dovrebbero essere sempre null, in caso contrario significherebbe che sono già state editate dal company operator e qui non dovrebbe neanche entrarci!
            partial.push(effettiveManualiFlat[index * 2]);
            partial.push(effettiveManualiFlat[index * 2 + 1]);
            MT.effettiveManualiFlat.push(partial);

            partial = []; // valorizzo coppia posizione valori nulli
            partial.push(nulls[index * 2] === null ? null : 0);
            partial.push(nulls[index * 2 + 1] === null ? null : 1);
            MT.nullPos.push(partial);
            MT.operazioneSuCoppia.push(nulls2ope(partial));
        });
    };

    static ripristinaDaRichiesta = (MT: ManutenzioneTimbrature, resource: Pick<PresenzeCausaleWFRecord, 'jsonTimbrature'>) => {
        MT.arrayCoppie = []; // array di array di una coppia (un solo elemento)
        MT.effettiveManualiFlat = []; // array di array di stringhe (max due elementi)
        MT.nullPos = []; // array di array di int (max due elementi)
        MT.maskEffettive = []; // array di int rappresentanti la mask della coppia corrispondente
        MT.operazioneSuCoppia = []; // array che contiene la coppia (se originale) altrimenti null
        const jsonTimbrature: JSonManutenzioneTimbrature = JSON.parse(resource.jsonTimbrature!);
        MT.effettiveOriginali = jsonTimbrature.effettiveOriginali;

        jsonTimbrature.timbratureWF.forEach(function (val, index) {
            if (index % 2 === 0) {
                const clone: CoppiaTimbratureEx = CoppiaTimbrature.factory({entrata: val});
                if (isDefined(jsonTimbrature.posizioneOriginali[index])) {
                    clone.posizioneEntrata = jsonTimbrature.posizioneOriginali[index]!;
                }
                clone.entrataManuale = jsonTimbrature.nulls[index]; // significativo solo per App, valuta se timbratura editabile
                clone.uscitaManuale = jsonTimbrature.nulls[index + 1]; // significativo solo per App, valuta se timbratura editabile
                clone.entrataTerminale = clone.entrataManuale ? 'null' : '*';
                clone.uscitaTerminale = clone.uscitaManuale ? 'null' : '*';
                MT.arrayCoppie.push([clone]);
                MT.nullPos.push([jsonTimbrature.nulls[index] ? 0 : null, jsonTimbrature.nulls[index + 1] ? 1 : null]);
                MT.effettiveManualiFlat.push([jsonTimbrature.manuali[index], jsonTimbrature.manuali[index + 1]]);

                // valorizzo maschera per coppia
                const indMask = MT.arrayCoppie.length - 1;
                let mask = 0x03 << indMask * 2;
                mask = mask & jsonTimbrature.maskEffettive;
                mask = mask >> indMask * 2;
                MT.maskEffettive.push(mask);
                clone.utilizzaEntrata = !!(mask & 0x01);
                clone.utilizzaUscita = !!(mask & 0x02);
            } else {
                const clone = MT.arrayCoppie[MT.arrayCoppie.length - 1][0];
                clone.uscita = val;
                if (isDefined(jsonTimbrature.posizioneOriginali[index])) {
                    clone.posizioneUscita = jsonTimbrature.posizioneOriginali[index]!;
                }
            }
        });
        MT.operazioneSuCoppia = MT.operazioneSuCoppia.concat(jsonTimbrature.operazioni);
    };

    /**
     * Aggiorna l'array elencoTimbratureGiornataOriginale
     * @param MT
     * @param dtGiornata
     * @param returnNull se true aggiunge la timbratura (entrata o uscita) anche se null, altrimenti non viene aggiunto nulla
     */
    static updStrisciataOriginale = (MT: ManutenzioneTimbrature, dtGiornata: FPNetDate, returnNull: boolean = false) => {
        //console.log('updStrisciataOriginale');
        const elencoTimbrature: InfoTimbratura[] = [];
        let indexOriginali = 0;
        const cercaPosizione = (val: CoppiaTimbratureEx[]) => {
            if ((indexOriginali % 2) === 0) {
                if (val[0].posizioneEntrata === indexOriginali) {
                    return true;
                }
            } else {
                if (val[0].posizioneUscita === indexOriginali) {
                    return true;
                }
            }
            return false;
        };

        MT.effettiveOriginali.forEach(function (val) {
            // Clono coppia timbratura originale!!!
            const clone: CoppiaTimbratureEx = CoppiaTimbrature.factory(val);

            let indexProposte;
            indexProposte = MT.arrayCoppie.findIndex(cercaPosizione);
            if (indexProposte >= 0) {
                if ((MT.maskEffettive[indexProposte] & 0x01) === 0) {
                    clone.entrataRimossa = true;
                }
            }
            ++indexOriginali;

            indexProposte = MT.arrayCoppie.findIndex(cercaPosizione);
            if (indexProposte >= 0) {
                if ((MT.maskEffettive[indexProposte] & 0x02) === 0) {
                    clone.uscitaRimossa = true;
                }
            }
            ++indexOriginali;
            addStrisciata(elencoTimbrature, dtGiornata, clone, returnNull);
        });
        return elencoTimbrature;
    };
    /**
     * Aggiorna l'array elencoTimbratureGiornata
     * @param MT
     * @param dtGiornata
     * @param returnNull se true aggiunge la timbratura (entrata o uscita) anche se null, altrimenti non viene aggiunto nulla
     */
    static updStrisciataProposta = (MT: ManutenzioneTimbrature, dtGiornata: FPNetDate, returnNull: boolean = false) => {
        //console.log('updAnteprimaStrisciata');
        const elencoTimbrature: InfoTimbratura[] = [];
        MT.arrayCoppie.forEach(function (val, index) {
            const clone: CoppiaTimbratureEx = CoppiaTimbrature.factory(val[0]);
            if ((MT.maskEffettive[index] & 0x01) === 0) {
                clone.entrata = null;
            }
            if ((MT.nullPos[index][0]) !== null) {
                clone.entrataManuale = true;
            }

            if ((MT.maskEffettive[index] & 0x02) === 0) {
                clone.uscita = null;
            }
            if ((MT.nullPos[index][1]) !== null) {
                clone.uscitaManuale = true;
            }
            addStrisciata(elencoTimbrature, dtGiornata, clone, returnNull);
        });
        return elencoTimbrature;
    };

    static updStrisciataOriginaleBkp = (resource: Pick<PresenzeCausaleWFRecord | PresenzeGiornataPlusRecord, 'effettiveOriginaliBackup'>, dtGiornata: FPNetDate,) => {
        const elenco: InfoTimbratura[] = [];
        if (resource.effettiveOriginaliBackup && resource.effettiveOriginaliBackup.length > 0) {
            for (let ind0 = 0; ind0 < resource.effettiveOriginaliBackup.length;) {
                const timb: CoppiaTimbratureEx = {
                    entrata: resource.effettiveOriginaliBackup[ind0++],
                    uscita: ind0 < resource.effettiveOriginaliBackup.length ? resource.effettiveOriginaliBackup[ind0++] : null,
                    utilizzaEntrata: true,
                    generaEntrata: false,
                    utilizzaUscita: true,
                    generaUscita: false,
                    entrataTerminale: "",
                    uscitaTerminale: ""
                };
                addStrisciata(elenco, dtGiornata, timb, false);
            }
        }
        return elenco;
    }

    static MT2JSON = (MT: ManutenzioneTimbrature) => {
        const jsonTimbrature: JSonManutenzioneTimbrature = {
            maskEffettive: 65535,
            timbratureWF: [], // Timbrature risultanti, array di stringhe, ogni elemento corrisponde a una timbratura
            nulls: [], // Posizioni nulle, array di boolean, ogni elemento rappresenta la posizioni editabile (true editabile, false => non editabile)
            operazioni: [], // array di interi, ogni elemento rappresenta l'operazione eseguibile per la coppia
            manuali: [], // Timbrature manuali, array di stringhe, ogni elemento corrisponde a una timbratura editata (null in corrispondenza di una timbratura effettiva)
            effettiveOriginali: [], // copia delle effettive
            posizioneOriginali: [], // posizione delle effettive
        };

        MT.arrayCoppie.forEach(function (val, index) {
            const coppia = val[0];
            if ((MT.maskEffettive[index] & 0x01) === 0) {
                jsonTimbrature.maskEffettive = jsonTimbrature.maskEffettive & ~(0x01 << (index * 2));
            }
            if ((MT.maskEffettive[index] & 0x02) === 0) {
                jsonTimbrature.maskEffettive = jsonTimbrature.maskEffettive & ~(0x02 << (index * 2));
            }
            jsonTimbrature.timbratureWF.push(coppia.entrata ? coppia.entrata : '');
            jsonTimbrature.posizioneOriginali.push(isDefined(coppia.posizioneEntrata) ? coppia.posizioneEntrata! : null);
            jsonTimbrature.timbratureWF.push(coppia.uscita ? coppia.uscita : '');
            jsonTimbrature.posizioneOriginali.push(isDefined(coppia.posizioneUscita) ? coppia.posizioneUscita! : null);

            jsonTimbrature.manuali.push(MT.effettiveManualiFlat[index][0]);
            jsonTimbrature.manuali.push(MT.effettiveManualiFlat[index][1]);

            jsonTimbrature.nulls.push(MT.nullPos[index][0] !== null);
            jsonTimbrature.nulls.push(MT.nullPos[index][1] !== null);
            jsonTimbrature.operazioni.push(MT.operazioneSuCoppia[index]);
        });
        jsonTimbrature.effettiveOriginali = MT.effettiveOriginali;
        return jsonTimbrature;
    };

    static rimuovi = (MT: ManutenzioneTimbrature, index: number) => {
        MT.arrayCoppie.splice(index, 1);
        MT.effettiveManualiFlat.splice(index, 1);
        MT.nullPos.splice(index, 1);
        MT.maskEffettive.splice(index, 1);
        MT.operazioneSuCoppia.splice(index, 1);
    };

    static aggiungi = (MT: ManutenzioneTimbrature, index: number | null) => {
        const coppia: CoppiaTimbratureEx = CoppiaTimbrature.factory();
        coppia.entrataManuale = true;
        coppia.uscitaManuale = true;
        if (isDefined(index) && index !== null) {
            MT.arrayCoppie.splice(index, 0, [coppia]);
            MT.effettiveManualiFlat.splice(index, 0, [null, null]);
            MT.nullPos.splice(index, 0, [0, 1]);
            MT.maskEffettive.splice(index, 0, 0x03);
            MT.operazioneSuCoppia.splice(index, 0, 1);
        } else {
            MT.arrayCoppie.push([coppia]);
            MT.effettiveManualiFlat.push([null, null]);
            MT.nullPos.push([0, 1]);
            MT.maskEffettive.push(0x03);
            MT.operazioneSuCoppia.push(1);
        }
    };

    static dividi = (MT: ManutenzioneTimbrature, index: number, coppiaDaDividere: CoppiaTimbratureEx) => {
        let coppia: CoppiaTimbratureEx = CoppiaTimbrature.factory({entrata: coppiaDaDividere.entrata, entrataTerminale: coppiaDaDividere.entrataTerminale});
        coppia.entrataManuale = false;
        coppia.uscitaManuale = true;
        coppia.posizioneEntrata = coppiaDaDividere.posizioneEntrata;
        MT.arrayCoppie.splice(index, 1, [coppia]);
        MT.effettiveManualiFlat.splice(index, 1, [null, null]);
        const saveNullPos = MT.nullPos[index];
        MT.nullPos.splice(index, 1, [saveNullPos[0], 1]);
        MT.maskEffettive.splice(index, 1, 0x03); // N.B. L'operazione di "divisione" della copia riabilita entrambe le timbrature
        MT.operazioneSuCoppia.splice(index, 1, 0);

        coppia = CoppiaTimbrature.factory({uscita: coppiaDaDividere.uscita, uscitaTerminale: coppiaDaDividere.uscitaTerminale});
        coppia.entrataManuale = true;
        coppia.uscitaManuale = false;
        coppia.posizioneUscita = coppiaDaDividere.posizioneUscita;
        MT.arrayCoppie.splice(index + 1, 0, [coppia]);
        MT.effettiveManualiFlat.splice(index + 1, 0, [null, null]);
        MT.nullPos.splice(index + 1, 0, [0, saveNullPos[1]]);
        MT.maskEffettive.splice(index + 1, 0, 0x03); // N.B. L'operazione di "divisione" della copia riabilita entrambe le timbrature
        MT.operazioneSuCoppia.splice(index + 1, 0, 0);
    };

    /**
     * Effettua il compare tra le timb effettive all'atto del caricamento della richiesta con quelle effettive da timbrature (backup), utile a intercettare travasi successivi al caricamento della richiesta
     * @param MT
     * @param day
     * @returns {boolean} true se diverse
     */
    static effettiveOriginaliIsChanged = (MT: ManutenzioneTimbrature, day: Pick<PresenzeCausaleWFRecord | PresenzeGiornataPlusRecord, 'effettiveOriginaliBackup'>) => {
        return !isTimbEqual(MT.effettiveOriginali, day);
    }
    /**
     * Effettua il compare tra le timb effettive con quelle effettive da timbrature (backup), utile a intercettare modifiche fatte dal COMPANY_OPERATOR
     * @param day
     * @returns {boolean} true se diverse
     */
    static effettiveIsChanged = (day: PresenzeGiornataPlusRecord) => {
        return !isTimbEqual(day.effettive, day);
    }
}

/**
 * Effettua il compare tra le timbrature in timb e le effettiveOriginaliBackup di day
 * @param timb array di coppie di timbrature {entrata, uscita}
 * @param day PresenzeGiornataPlus {effettiveOriginaliBackup}
 * @returns {boolean}
 */
function isTimbEqual(timb: Pick<CoppiaTimbrature, 'entrata' | 'uscita'>[], day: Pick<PresenzeCausaleWFRecord | PresenzeGiornataPlusRecord, 'effettiveOriginaliBackup'>) {
    const flat: string[] = [];
    if (timb)
        timb.forEach(function (val) {
            //if (val.entrata && val.entrata !== 'null')
            {
                flat.push(val.entrata || 'null');
            }
            //if (val.uscita && val.uscita !== 'null')
            {
                flat.push(val.uscita || 'null');
            }
        });
    const flatOriginali: string[] = [];
    if (day.effettiveOriginaliBackup) {
        day.effettiveOriginaliBackup.forEach(function (val) {
            //if (val && val !== 'null')
            {
                flatOriginali.push(val || 'null');
            }
        });
    }
    if ((flatOriginali.length % 2) !== 0) {
        flatOriginali.push('null');
    }

    const effettive = JSON.stringify(flat);
    const effettiveOriginaliBackup = JSON.stringify(flatOriginali);
    return effettive.localeCompare(effettiveOriginaliBackup) === 0;
}

/**
 * Aggiunge la coppia all'array elencoTimbrature
 * @param elencoTimbrature array in cui inserire la coppia
 * @param dtGiornata
 * @param clone la coppia da aggiungere
 * @param returnNull se true aggiunge la timbratura (entrata o uscita) anche se null, altrimenti non viene aggiunto nulla
 */
function addStrisciata(elencoTimbrature: InfoTimbratura[], dtGiornata: FPNetDate, clone: CoppiaTimbratureEx, returnNull: boolean) {
    let tsTimbratura = DateTime.fromISO(dtGiornata!);
    const entrataIsValid = clone.entrata !== null && clone.entrata !== 'null' && clone.entrata.length === 4;
    const uscitaIsValid = clone.uscita !== null && clone.uscita !== 'null' && clone.uscita.length === 4;

    if (entrataIsValid) {
        const hour = Number.parseInt(clone.entrata!.substring(0, 2));
        const minute = Number.parseInt(clone.entrata!.substring(2));
        tsTimbratura = tsTimbratura.set({
            hour,
            minute,
            second: 0,
            millisecond: 0
        });
        elencoTimbrature.push({verso: 'ENTRATA', tsTimbratura: tsTimbratura.toISO(), manuale: !!clone.entrataManuale, rimossa: !!clone.entrataRimossa,});
    } else {
        if (returnNull) {// || uscitaIsValid) {
            elencoTimbrature.push({verso: 'ENTRATA', tsTimbratura: null});
        }
    }

    if (uscitaIsValid) {
        const hour = Number.parseInt(clone.uscita!.substring(0, 2));
        const minute = Number.parseInt(clone.uscita!.substring(2));
        tsTimbratura = tsTimbratura.set({
            hour,
            minute,
            second: 0,
            millisecond: 0
        });
        elencoTimbrature.push({verso: 'USCITA', tsTimbratura: tsTimbratura.toISO(), manuale: !!clone.uscitaManuale, rimossa: !!clone.uscitaRimossa,});
    } else {
        if (returnNull) {// || entrataIsValid) {
            elencoTimbrature.push({verso: 'USCITA', tsTimbratura: null});
        }
    }
}

function nulls2ope(nulls: number[]) {
    return nulls[0] === null && nulls[1] === null ? 2 : 0;
}
